/**
 * Error message utility for CRUD operations
 * Provides user-friendly error messages for common error scenarios
 *
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 */

/**
 * Standard error response from backend
 */
interface BackendErrorResponse {
  success: false;
  error: string;
  message: string;
  details?: any;
}

/**
 * Extracts a user-friendly error message from various error types
 *
 * @param error - The error object (can be Error, unknown, or any type)
 * @returns A user-friendly error message string
 *
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle backend error responses with message field (new format)
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as any;

    // Check for backend error response format
    if (errorObj.message && typeof errorObj.message === 'string') {
      return errorObj.message;
    }

    // Check for error property
    if (errorObj.error && typeof errorObj.error === 'string') {
      return errorObj.error;
    }

    // Check for HTTP status codes
    if (errorObj.status) {
      if (errorObj.status === 401) {
        return 'Your session has expired. Please log in again.';
      }
      if (errorObj.status === 403) {
        return "You don't have permission to perform this action.";
      }
      if (errorObj.status === 404) {
        return 'Resource not found.';
      }
      if (errorObj.status >= 500) {
        return 'Server error. Please try again later.';
      }
      if (errorObj.status === 400 && errorObj.message) {
        return errorObj.message;
      }
    }
  }

  // Handle Error instances
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Network errors (Requirement 14.1)
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('econnrefused') ||
      message.includes('etimedout') ||
      message.includes('connection')
    ) {
      return 'Network error. Please check your connection and try again.';
    }

    // Authorization errors - 401 Unauthorized (Requirement 14.2)
    if (message.includes('401') || message.includes('unauthorized')) {
      return 'Your session has expired. Please log in again.';
    }

    // Authorization errors - 403 Forbidden (Requirement 14.2)
    if (message.includes('403') || message.includes('forbidden')) {
      return "You don't have permission to perform this action.";
    }

    // Not found errors - 404
    if (message.includes('404') || message.includes('not found')) {
      return 'Resource not found.';
    }

    // Server errors - 500+ (Requirement 14.1)
    if (
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504') ||
      message.includes('server error') ||
      message.includes('internal server')
    ) {
      return 'Server error. Please try again later.';
    }

    // Validation errors - pass through the message (Requirement 14.3)
    // If the error message looks user-friendly (doesn't contain technical terms),
    // return it as-is
    if (
      message.includes('validation') ||
      message.includes('invalid') ||
      message.includes('required') ||
      message.includes('must be') ||
      message.includes('cannot be') ||
      message.includes('should be')
    ) {
      return error.message;
    }

    // If the error message is reasonably short and doesn't look like a stack trace
    // or technical error, return it
    if (error.message.length < 200 && !message.includes('at ') && !message.includes('stack')) {
      return error.message;
    }
  }

  // Unknown error fallback (Requirement 14.4)
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Categorizes error types for different handling strategies
 */
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NETWORK = 'network',
  SERVER = 'server',
  UNKNOWN = 'unknown',
}

/**
 * Determines the category of an error
 */
export const categorizeError = (error: unknown): ErrorCategory => {
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as any;

    if (
      errorObj.status === 401 ||
      errorObj.error?.includes('token') ||
      errorObj.error?.includes('session')
    ) {
      return ErrorCategory.AUTHENTICATION;
    }

    if (errorObj.status === 403 || errorObj.error?.includes('permission')) {
      return ErrorCategory.AUTHORIZATION;
    }

    if (
      errorObj.status === 400 ||
      errorObj.error?.includes('validation') ||
      errorObj.error?.includes('invalid')
    ) {
      return ErrorCategory.VALIDATION;
    }

    if (errorObj.status >= 500) {
      return ErrorCategory.SERVER;
    }
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (
      message.includes('network') ||
      message.includes('connection') ||
      message.includes('timeout')
    ) {
      return ErrorCategory.NETWORK;
    }

    if (
      message.includes('401') ||
      message.includes('unauthorized') ||
      message.includes('session')
    ) {
      return ErrorCategory.AUTHENTICATION;
    }

    if (
      message.includes('403') ||
      message.includes('forbidden') ||
      message.includes('permission')
    ) {
      return ErrorCategory.AUTHORIZATION;
    }

    if (
      message.includes('validation') ||
      message.includes('invalid') ||
      message.includes('required')
    ) {
      return ErrorCategory.VALIDATION;
    }

    if (message.includes('500') || message.includes('server')) {
      return ErrorCategory.SERVER;
    }
  }

  return ErrorCategory.UNKNOWN;
};
